import type { ComponentProps, KeyboardEvent } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Input } from '@/shared/ui/primitives/input'
import { cn } from '@/shared/ui/cn'

export type NumberInputMode = 'integer' | 'decimal' | 'signed'

export type NumberInputProps = {
  value: string
  onChange: (value: string) => void
  mode: NumberInputMode
  step?: number
  min?: number
  max?: number
  showSteppers?: boolean
  id?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  onFocus?: ComponentProps<'input'>['onFocus']
  onBlur?: ComponentProps<'input'>['onBlur']
  'aria-label'?: string
  'aria-invalid'?: ComponentProps<'input'>['aria-invalid']
  'aria-describedby'?: ComponentProps<'input'>['aria-describedby']
}

const DEFAULT_STEP = 1

const STEPPER_CLASS =
  'flex flex-1 items-center justify-center text-fg-dim transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50'

function sanitizeDecimal(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '')
  const firstDot = cleaned.indexOf('.')
  if (firstDot === -1) return cleaned
  const head = cleaned.slice(0, firstDot + 1)
  const tail = cleaned.slice(firstDot + 1).replace(/\./g, '')
  return head + tail
}

function sanitize(raw: string, mode: NumberInputMode): string {
  if (mode === 'integer') return raw.replace(/[^\d]/g, '')
  if (mode === 'decimal') return sanitizeDecimal(raw)
  const stripped = raw.replace(/[^\d.-]/g, '')
  const negative = stripped.startsWith('-')
  const body = sanitizeDecimal(stripped)
  return negative ? '-' + body : body
}

function lowerBound(mode: NumberInputMode, min?: number): number | undefined {
  if (mode === 'signed') return min
  return min === undefined ? 0 : Math.max(min, 0)
}

function clamp(value: number, min?: number, max?: number): number {
  if (min !== undefined && value < min) return min
  if (max !== undefined && value > max) return max
  return value
}

function decimalPlaces(value: number): number {
  const text = String(value)
  const separator = text.indexOf('.')
  return separator === -1 ? 0 : text.length - separator - 1
}

function stepValue(
  value: string,
  delta: number,
  mode: NumberInputMode,
  min?: number,
  max?: number,
): string | null {
  const current = Number.parseFloat(value)
  const hasCurrent = Number.isFinite(current)
  if (!hasCurrent && delta < 0) return null
  const base = hasCurrent ? current : 0
  const next = clamp(base + delta, lowerBound(mode, min), max)
  const places =
    mode === 'integer' ? 0 : Math.max(decimalPlaces(base), decimalPlaces(delta))
  return String(Number(next.toFixed(places)))
}

export function NumberInput({
  value,
  onChange,
  mode,
  step = DEFAULT_STEP,
  min,
  max,
  showSteppers = true,
  id,
  placeholder,
  disabled,
  className,
  onFocus,
  onBlur,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: NumberInputProps) {
  function applyStep(delta: number) {
    const next = stepValue(value, delta, mode, min, max)
    if (next === null || next === value) return
    onChange(next)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return
    event.preventDefault()
    applyStep(event.key === 'ArrowUp' ? step : -step)
  }

  const field = (
    <Input
      id={id}
      type="text"
      inputMode={mode === 'integer' ? 'numeric' : 'decimal'}
      value={value}
      onChange={(e) => onChange(sanitize(e.target.value, mode))}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(showSteppers && 'rounded-r-none', className)}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
    />
  )

  if (!showSteppers) return field

  const target = ariaLabel ?? 'value'

  return (
    <div className="flex items-stretch">
      {field}
      <div className="flex w-7 shrink-0 flex-col overflow-hidden rounded-r-md border border-l-0 border-input">
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label={`Increase ${target}`}
          onClick={() => applyStep(step)}
          className={STEPPER_CLASS}
        >
          <ChevronUp aria-hidden className="size-3" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label={`Decrease ${target}`}
          onClick={() => applyStep(-step)}
          className={cn(STEPPER_CLASS, 'border-t border-input')}
        >
          <ChevronDown aria-hidden className="size-3" />
        </button>
      </div>
    </div>
  )
}
